import { DeviceType, deviceTypesWithAccurateBatteryPercentage } from '@ixicare/resources';
import { BatteryIndicator, formatDate, Link, OfflineDevice } from '@ixicare/ui';
import { t } from '~i18n';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { BEACON_LAST_SEEN_THRESHOLD, LAST_SEEN_THRESHOLD } from '~modules/devices/utils/constants';

export type TransformContext = {
  isArchived?: boolean;
  siteId?: number;
  showArchiveColumn?: boolean;
  showTypeColumn?: boolean;
};

function ownerCell(owner: any) {
  if (!owner) return undefined;
  const name = `${owner.firstName} ${owner.lastName}`;
  return owner.id ? <Link href={`/residents/${owner.id}`}>{name}</Link> : name;
}

function lastSeenCell(date: any, threshold: Date = LAST_SEEN_THRESHOLD) {
  if (!date || !formatDate(date)) return undefined;
  return new Date(date) >= threshold ? formatDate(date) : <OfflineDevice date={date} />;
}

export const BUTTON_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.emergencyButton'),
  t('devices.table.installationZone'),
  t('devices.table.description'),
  t('devices.table.softwareVersion'),
  t('devices.table.batteryLevel'),
  t('devices.table.lastSeen'),
];

export const BUTTON_COLUMN_IDS = [
  'mac_address',
  'emergency_button',
  'room_number',
  'description',
  'sw_version',
  'battery_level',
  'updated_on',
];

export const BUTTON_COLUMNS_WITH_TYPE_AND_ARCHIVE = [
  t('devices.table.deviceType'),
  ...BUTTON_COLUMNS,
  t('devices.table.isArchived'),
];

export const API_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.description'),
  t('devices.table.apiIdentifier'),
  t('devices.table.installationZone'),
  t('devices.table.lastSeen'),
];

export const API_COLUMN_IDS = ['mac_address', 'description', 'api_identifier', 'room_number', 'updated_on'];

export const WEARABLE_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.owner'),
  t('devices.table.deviceType'),
  t('devices.table.description'),
  t('global.phoneNumber'),
  t('devices.table.softwareVersion'),
  t('devices.table.batteryLevel'),
  t('devices.table.imeiNumber'),
  t('devices.table.lastSeen'),
];

export const WEARABLE_COLUMN_IDS = [
  'mac_address',
  'owner',
  'device_type',
  'description',
  'phone_number',
  'sw_version',
  'battery_level',
  'imei_number',
  'updated_on',
];

export const WEARABLE_COLUMNS_WITH_TYPE_AND_ARCHIVE = [
  t('devices.table.deviceType'),
  ...WEARABLE_COLUMNS,
  t('devices.table.isArchived'),
];

export const GATEWAY_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.description'),
  t('devices.table.generatedOn'),
  t('devices.table.installationZone'),
  t('devices.table.lastSeen'),
];

export const GATEWAY_COLUMN_IDS = ['mac_address', 'description', 'created', 'installation_zone', 'last_seen'];

export const GATEWAY_COLUMNS_WITH_TYPE_AND_ARCHIVE = [
  t('devices.table.deviceType'),
  ...GATEWAY_COLUMNS,
  t('devices.table.isArchived'),
];

export const BEACON_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.description'),
  t('devices.table.generatedOn'),
  t('devices.table.batteryLevel'),
  t('devices.table.installationZone'),
  t('devices.table.calibrationFactor'),
  t('devices.table.lastSeen'),
];

export const BEACON_COLUMN_IDS = [
  'mac_address',
  'description',
  'created',
  'battery_level',
  'installation_zone',
  'calibration_factor',
  'last_seen',
];

export const BEACON_COLUMNS_WITH_TYPE_AND_ARCHIVE = [
  t('devices.table.deviceType'),
  ...BEACON_COLUMNS,
  t('devices.table.isArchived'),
];

export function transformButtonToRow(device: any, context: TransformContext) {
  const { isArchived = false, showArchiveColumn = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push('Button');
  }

  row.push(
    device.macAddress,
    device.subType != null ? 'Active' : 'Inactive',
    device.roomNumber,
    device.description,
    device.swVersion,
    typeof device.batteryLevel === 'number' ? (
      <BatteryIndicator
        inaccurate={!deviceTypesWithAccurateBatteryPercentage.includes(device.deviceType)}
        percentage={device.batteryLevel as number}
        chargerConnected={device.chargerConnected}
      />
    ) : undefined,
    lastSeenCell(device.status?.updatedOn)
  );

  if (showArchiveColumn) {
    row.push(isArchived ? 'Archived' : 'Unarchived');
  }

  return row;
}

export function transformWearableToRow(device: any, context: TransformContext) {
  const { isArchived = false, showArchiveColumn = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push('Wearable');
  }

  row.push(
    device.macAddress,
    ownerCell(device.owner),
    device.deviceType && t(`device.types.${device.deviceType.toLowerCase()}`),
    device.description,
    device.phoneNumber,
    device.swVersion?.match(/\d+(\.\d+)+/)?.[0],
    typeof device.batteryLevel === 'number' ? (
      <BatteryIndicator
        inaccurate={!deviceTypesWithAccurateBatteryPercentage.includes(device.deviceType)}
        percentage={device.batteryLevel as number}
        chargerConnected={device.chargerConnected}
      />
    ) : undefined,
    device.imeiNumber,
    lastSeenCell(device.status?.updatedOn)
  );

  if (showArchiveColumn) {
    row.push(isArchived ? 'Archived' : 'Unarchived');
  }

  return row;
}

export const EVIEW_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.owner'),
  t('devices.table.description'),
  t('global.phoneNumber'),
  t('devices.table.batteryLevel'),
  t('devices.table.imeiNumber'),
  t('devices.table.lastSeen'),
];

export const EVIEW_COLUMN_IDS = [
  'mac_address',
  'owner',
  'description',
  'phone_number',
  'battery_level',
  'imei_number',
  'updated_on',
];

export const EVIEW_COLUMNS_WITH_TYPE_AND_ARCHIVE = [
  t('devices.table.deviceType'),
  ...EVIEW_COLUMNS,
  t('devices.table.isArchived'),
];

export function transformEviewToRow(device: any, context: TransformContext) {
  const { isArchived = false, showArchiveColumn = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push(device.deviceType === DeviceType.TWIG ? 'Twig' : 'Eview');
  }

  row.push(
    device.macAddress,
    ownerCell(device.owner),
    device.description,
    device.phoneNumber,
    typeof device.batteryLevel === 'number' ? (
      <BatteryIndicator
        inaccurate={!deviceTypesWithAccurateBatteryPercentage.includes(device.deviceType)}
        percentage={device.batteryLevel as number}
        chargerConnected={device.chargerConnected}
      />
    ) : undefined,
    device.imeiNumber,
    lastSeenCell(device.status?.updatedOn)
  );

  if (showArchiveColumn) {
    row.push(isArchived ? 'Archived' : 'Unarchived');
  }

  return row;
}

// API_EXTERNAL devices are pre-registered through the public API and identified by
// their `apiIdentifier` (camelized from the backend's `api_identifier`) rather than IMEI/phone.
export function transformApiToRow(device: any, context: TransformContext) {
  const { isArchived = false, showArchiveColumn = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push(t('device.types.api_external'));
  }

  row.push(
    device.macAddress,
    device.description,
    device.apiIdentifier,
    device.roomNumber,
    lastSeenCell(device.status?.updatedOn)
  );

  if (showArchiveColumn) {
    row.push(isArchived ? 'Archived' : 'Unarchived');
  }

  return row;
}

export function transformGatewayToRow(gateway: any, context: TransformContext) {
  const { isArchived = false, showArchiveColumn = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push('Gateway');
  }

  row.push(
    gateway.macAddress,
    gateway.description,
    gateway.created && formatDate(gateway.created),
    gateway.zone?.name,
    lastSeenCell(gateway.lastSeen)
  );

  if (showArchiveColumn) {
    row.push(isArchived ? 'Archived' : 'Unarchived');
  }

  return row;
}

export function transformBeaconToRow(beacon: any, context: TransformContext) {
  const { isArchived = false, showArchiveColumn = false, showTypeColumn = false } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push('Beacon');
  }

  row.push(
    beacon.macAddress,
    beacon.description,
    beacon.created ? formatDate(beacon.created) : undefined,
    typeof beacon.batteryLevel === 'number' ? (
      <BatteryIndicator inaccurate percentage={beacon.batteryLevel as number} />
    ) : undefined,
    beacon.zone?.name,
    beacon.calibrationFactor === 2 || beacon.calibrationFactor === 0.0 ? '0' : beacon.calibrationFactor,
    lastSeenCell(beacon.lastSeen, BEACON_LAST_SEEN_THRESHOLD)
  );

  if (showArchiveColumn) {
    row.push(isArchived ? 'Archived' : 'Unarchived');
  }

  return row;
}

export const DBG_COLUMNS = [
  t('devices.table.macAddress'),
  t('devices.table.deviceType'),
  t('devices.table.description'),
];

export const DBG_COLUMN_IDS = ['mac_address', 'device_type', 'description'];

export function transformDBGToRow(dbg: DBGWithType, context: TransformContext) {
  const { isArchived = false, showArchiveColumn = false, showTypeColumn = true } = context;

  const row: any[] = [];

  if (showTypeColumn) {
    row.push(dbg.type);
  }

  row.push(dbg.macAddress, dbg.type, dbg.description);

  if (showArchiveColumn) {
    row.push(isArchived ? 'Archived' : 'Unarchived');
  }

  return row;
}
